import React, { Fragment } from 'react';
// ───────────────────────────────────────────────────────────────────────────
// Internal review page: editorial directions for the integration sphere.
// Visit /sphere-directions to review. Not linked from the marketing nav.
// ───────────────────────────────────────────────────────────────────────────
type NodeRenderer = (i: number) => React.ReactNode;
// Places `count` items evenly on a circle of `radius` around the stage center.
// Pure CSS positioning (left/top 50% + translate) so no measuring is needed.
function Ring({
  count,
  radius,
  render,
  connectorClass
}: {
  count: number;
  radius: number;
  render: NodeRenderer;
  connectorClass?: string;
}) {
  const items = Array.from({
    length: count
  });
  return <>
      {items.map((_, i) => {
      const ang = i / count * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(ang) * radius;
      const y = Math.sin(ang) * radius;
      return <Fragment key={i} data-id="element-11548" data-mp="3rtmvushc2h1l" data-mpi={i}>
            {connectorClass && <div className={connectorClass} style={{
          width: radius,
          transform: `rotate(${ang * 180 / Math.PI}deg)`
        }} data-id="element-11549" data-mp="1y5uyvda3b7hi" />}
            <div className="absolute flex items-center justify-center" style={{
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
        }} data-id="element-11550" data-mp="1t607z9qbehwg">
              {render(i)}
            </div>
          </Fragment>;
    })}
    </>;
}
const PALETTE = ['#4A154B', '#0A66C2', '#00A1E0', '#FF7A59', '#635BFF', '#EA4335', '#0F9D58', '#F24E1E', '#5E6AD2', '#E01563', '#1264A3'];
const CRITIQUE = [{
  n: '01',
  t: 'Look-alike glass bubbles',
  b: 'Every node is the same milky translucent circle, so the eye reads texture, not meaning.'
}, {
  n: '02',
  t: 'The dashed-line + glowing-dot trope',
  b: 'It’s the default “AI network” cliché — instantly familiar, never premium.'
}, {
  n: '03',
  t: 'Muddied logos',
  b: 'Desaturating brand marks to one brown kills recognition and contrast — they look broken, not tasteful.'
}, {
  n: '04',
  t: 'Garish central glow',
  b: 'The heavy orange bloom dominates and flattens depth instead of guiding the eye.'
}, {
  n: '05',
  t: 'No editorial anchoring',
  b: 'There’s no typographic structure, hierarchy, or framing — it floats as decoration.'
}, {
  n: '06',
  t: 'Crowding',
  b: '~40 equal-weight nodes give density but no focal point; “a lot” reads as noise.'
}];
function Direction({
  label,
  title,
  principle,
  body,
  moves,
  stage
}: {
  label: string;
  title: string;
  principle: string;
  body: string;
  moves: string[];
  stage: React.ReactNode;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-2 border border-[#E7E0D7] rounded-3xl overflow-hidden bg-white" data-id="element-11551" data-mp="2mzhfw05ywovu">
      <div className="relative min-h-[340px] flex items-center justify-center overflow-hidden" data-id="element-11552" data-mp="2r12np73nacz6">
        {stage}
      </div>
      <div className="p-8 md:p-11" data-id="element-11553" data-mp="l84dbhrh2t2y">
        <span className="inline-block font-mono text-[11px] tracking-[0.22em] uppercase text-[#EA580C] mb-3" data-id="element-11554" data-mp="p8o6ka8xdcjt">
          {label}
        </span>
        <h3 className="font-title text-2xl font-semibold text-[#2B211A] tracking-tight mb-3" data-id="element-11555" data-mp="29ri81knzft02">
          {title}
        </h3>
        <p className="font-title italic text-[#2B211A] text-[17px] mb-4" data-id="element-11556" data-mp="25najpolfsf2d">
          {principle}
        </p>
        <p className="text-[#6B5D52] text-[15.5px] leading-relaxed" data-id="element-11557" data-mp="2jimpcr14rpy2">{body}</p>
        <ul className="mt-6 space-y-2.5" data-id="element-11558" data-mp="2eoqtpv8caf62">
          {moves.map(m => <li key={m} className="relative pl-5 text-[14.5px] text-[#2B211A] leading-relaxed" data-id="element-11559" data-mp="2te6fsxwxdwff">
              <span className="absolute left-0 top-[9px] w-[7px] h-[7px] rounded-full border-[1.5px] border-[#EA580C]" data-id="element-11560" data-mp="2ogo5p4769b5u" />
              {m}
            </li>)}
        </ul>
      </div>
    </div>;
}
export function SphereDirections() {
  return <main className="w-full min-h-screen bg-[#FAF8F4] text-[#2B211A]" data-id="element-11561" data-mp="2xh2rs5rymuae">
      <div className="max-w-[1100px] mx-auto px-6 md:px-7 py-16 md:py-24" data-id="element-11562" data-mp="180tp4r438fkb">
        <p className="font-mono text-[12px] tracking-[0.32em] uppercase text-[#EA580C] mb-5" data-id="element-11563" data-mp="132s440n3q667">
          Giga · Visual Direction Study
        </p>
        <h1 className="font-title font-medium text-[clamp(34px,6vw,60px)] leading-[1.04] tracking-tight max-w-[16ch] mb-6" data-id="element-11564" data-mp="15gni2ak6g8zj">
          Making the integration sphere feel editorial.
        </h1>
        <p className="text-[19px] text-[#6B5D52] max-w-[62ch] leading-relaxed" data-id="element-11565" data-mp="1ej6iko0hq9im">
          Four distinct directions for the “plugs into your world” constellation
          — each a deliberate move away from the generic AI-network look toward
          something that feels designed, premium, and unmistakably Giga. Review
          and pick one (or a blend) before we touch the live section.
        </p>
        <p className="mt-9 pt-6 border-t border-[#E7E0D7] font-mono text-[12px] text-[#9C8E82] tracking-wide" data-id="element-11566" data-mp="vpsvt66xl9e3">
          DRAFT · For review only · No code in the live section has been
          changed.
        </p>

        {/* Critique */}
        <section className="mt-20 bg-[#2B211A] text-[#efe9e1] rounded-3xl p-10 md:p-14" data-id="element-11567" data-mp="81t6map9pr1m">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#f5b98a] mb-3" data-id="element-11568" data-mp="7zgg395g28cy">
            Why it reads cheap today
          </p>
          <h2 className="font-title font-medium text-white text-[clamp(24px,3.4vw,34px)] tracking-tight" data-id="element-11569" data-mp="3rpmo17sotwm9">
            The honest diagnosis
          </h2>
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-9 gap-y-7" data-id="element-11570" data-mp="3mrbeky6b1o4k">
            {CRITIQUE.map(c => <div key={c.n} data-id="element-11571" data-mp="3dok00pi4uc8p">
                <span className="font-mono text-[12px] text-[#EA580C] tracking-wider" data-id="element-11572" data-mp="3dpvcretk2uby">
                  {c.n}
                </span>
                <p className="mt-1.5 text-[15px] text-[#cdbfb2] leading-relaxed" data-id="element-11573" data-mp="38w0lnqbmfd2u">
                  <strong className="text-white font-semibold" data-id="element-11574" data-mp="33ziiuvzupot5">{c.t}.</strong>{' '}
                  {c.b}
                </p>
              </div>)}
          </div>
        </section>

        {/* Directions */}
        <div className="mt-14 space-y-10" data-id="element-11575" data-mp="2u01ganfwbr1e">
          {/* A — Almanac */}
          <Direction label="Direction A · The Almanac" title="Engraved star chart" principle="Treat the stack like a celestial map in an old almanac." body="Hairline concentric rings, thin radial spokes, and small ivory nodes with mono captions on an ink-black field. It trades “tech glow” for the quiet authority of a printed chart — restrained, intelligent, and very ownable." moves={['Ink-black ground, ivory nodes, a single warm accent', 'Mono labels — names you can actually read', 'Concentric rings imply orbit without literal spinning', 'The Giga core is the only saturated thing']} stage={<div className="absolute inset-0 bg-[#1c1712]" data-id="element-11577" data-mp="333ydxhu41dq8">
                {[150, 250, 340].map(d => <div key={d} className="absolute left-1/2 top-1/2 rounded-full border border-white/[0.14]" style={{
            width: d,
            height: d,
            transform: 'translate(-50%, -50%)'
          }} data-id="element-11578" data-mp="2pctm31wzhpqg" />)}
                <Ring count={9} radius={125} connectorClass="absolute left-1/2 top-1/2 h-px origin-left bg-gradient-to-r from-[rgba(245,239,233,0.28)] to-transparent" render={i => <div className="w-[30px] h-[30px] rounded-full bg-[#efe9e1] text-[#1c1712] font-mono text-[11px] font-medium flex items-center justify-center shadow-[0_2px_10px_rgba(0,0,0,0.4)]" data-id="element-11580" data-mp="v9684wzru0">
                      {'SLNFHGZAMK'[i % 10]}
                    </div>} data-id="element-11579" data-mp="3hzxtfdjjug5e" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[52px] h-[52px] rounded-full bg-[#EA580C] text-white font-title italic text-[22px] flex items-center justify-center shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_0_34px_rgba(234,88,12,0.45)]" data-id="element-11581" data-mp="9t0ytb7p1x61">
                  G
                </div>
              </div>} data-id="element-11576" data-mp="2q0ak247loiph" />

          {/* B — Gallery */}
          <Direction label="Direction B · The Gallery" title="Curated specimens" principle="Fewer tiles, beautifully finished, in real color." body="Drop to ~10–14 hero integrations on a warm paper ground. Each is a crisp specimen card with the brand’s true color and a small label. Generous negative space and one elegant orbit ring — confidence through restraint." moves={['Paper-tone ground — bright, premium, magazine-like', 'Full-color brand marks, legible labels', 'Big breathing room; quality over quantity', 'Ink core anchors the warm palette']} stage={<div className="absolute inset-0 bg-[#F4F0EA]" data-id="element-11583" data-mp="30xnvisac4m0d">
                {[230, 330].map(d => <div key={d} className="absolute left-1/2 top-1/2 rounded-full border border-[#E7E0D7]" style={{
            width: d,
            height: d,
            transform: 'translate(-50%, -50%)'
          }} data-id="element-11584" data-mp="30vktf9vwr2fa" />)}
                <Ring count={8} radius={150} render={i => <div className="w-16 h-16 rounded-[18px] bg-white border border-[#E7E0D7] shadow-[0_14px_30px_-16px_rgba(43,33,26,0.35)] flex flex-col items-center justify-center gap-1" data-id="element-11586" data-mp="3fkqqosb6qikj">
                      <div className="w-[22px] h-[22px] rounded-[7px]" style={{
              background: PALETTE[i % PALETTE.length]
            }} data-id="element-11587" data-mp="3olozrp0hdg9z" />
                      <span className="text-[8px] font-semibold tracking-wide text-[#6B5D52]" data-id="element-11588" data-mp="1u8z5h667a522">
                        Brand
                      </span>
                    </div>} data-id="element-11585" data-mp="3flsfs0p1d5l9" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[84px] h-[84px] rounded-[24px] bg-[#2B211A] text-white font-title italic text-[30px] flex items-center justify-center shadow-[0_24px_50px_-20px_rgba(43,33,26,0.6)]" data-id="element-11589" data-mp="1mrj2qs8b4ql0">
                  G
                </div>
              </div>} data-id="element-11582" data-mp="3n7e4dzbx10jo" />

          {/* C — Blueprint */}
          <Direction label="Direction C · The Blueprint" title="Technical schematic" principle="Render the network like a precise engineering drawing." body="A measured grid, exact thin connectors, numbered nodes, mono labels, and one accent for the Giga core. It leans into Giga’s “actually does the work” positioning — engineered, deliberate, trustworthy rather than dreamy." moves={['Faint grid + measured, straight connectors', 'Numbered nodes like a parts diagram', 'Cool ink ground, single warm accent on the core', 'Reads “precision,” pairs well with the product story']} stage={<div className="absolute inset-0 bg-[#161a1d]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '26px 26px'
        }} data-id="element-11591" data-mp="1nkebg3pftoaa">
                <Ring count={10} radius={140} connectorClass="absolute left-1/2 top-1/2 h-px origin-left bg-[rgba(120,200,255,0.4)]" render={i => <div className="w-[26px] h-[26px] rounded border border-[rgba(150,210,255,0.7)] bg-[rgba(20,30,38,0.85)] text-[#cfeaff] font-mono text-[9px] flex items-center justify-center" data-id="element-11593" data-mp="277vla4me9t1g">
                      {String(i + 1).padStart(2, '0')}
                    </div>} data-id="element-11592" data-mp="1sjr08tzvd5fi" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60px] h-[60px] rounded-lg border-[1.5px] border-[#EA580C] bg-[rgba(234,88,12,0.14)] text-[#ffd9c2] font-mono text-[11px] tracking-widest flex items-center justify-center" data-id="element-11594" data-mp="2ba8n4hy0t6sy">
                  GIGA
                </div>
              </div>} data-id="element-11590" data-mp="25n954gk5jozf" />

          {/* D — Refined Constellation */}
          <Direction label="Direction D · The Refined Constellation" title="Same idea, elevated" principle="Keep the orbit — fix the materials, color, and depth." body="The lowest-risk path: keep the rotating constellation but give it a cohesive warm palette, real logo color on subtly tonal tiles, depth via scale + soft blur (not garish glow), and refined hairline connectors. The premium version of what’s already there." moves={['Warm graded ground, no harsh radial bloom', 'Real brand color, tasteful tonal tiles', 'Depth from scale & blur, not opacity mush', 'Solid hairline connectors with soft falloff']} stage={<div className="absolute inset-0" style={{
          background: 'radial-gradient(120% 100% at 50% 42%, #3a2c22 0%, #241b15 55%, #1a130f 100%)'
        }} data-id="element-11596" data-mp="20urecs23rwdm">
                <Ring count={11} radius={145} connectorClass="absolute left-1/2 top-1/2 h-px origin-left bg-gradient-to-r from-[rgba(255,196,150,0.5)] to-transparent" render={i => <div className="w-[46px] h-[46px] rounded-[14px] border border-white/60 shadow-[0_10px_24px_-10px_rgba(0,0,0,0.55)] flex items-center justify-center" style={{
            background: 'linear-gradient(150deg, rgba(255,251,247,0.96), rgba(244,236,228,0.86))'
          }} data-id="element-11598" data-mp="1bztymjrmzxf1">
                      <div className="w-[18px] h-[18px] rounded-[6px]" style={{
              background: PALETTE[i % PALETTE.length]
            }} data-id="element-11599" data-mp="y0c2tkfxbjw6" />
                    </div>} data-id="element-11597" data-mp="3v6xnzmfcm53t" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70px] h-[70px] rounded-[20px] bg-[#EA580C] text-white font-title italic text-[26px] flex items-center justify-center shadow-[0_0_0_8px_rgba(234,88,12,0.14),0_0_50px_rgba(234,88,12,0.4)]" data-id="element-11600" data-mp="1qogceh1m8h45">
                  G
                </div>
              </div>} data-id="element-11595" data-mp="2slnp9n4lrqy9" />
        </div>

        {/* Recommendation */}
        <div className="mt-20 p-8 md:p-10 border border-dashed border-[#E7E0D7] rounded-2xl bg-white text-[15px] text-[#6B5D52] leading-relaxed" data-id="element-11601" data-mp="2efvj0m0fdkl7">
          <strong className="text-[#2B211A]" data-id="element-11602" data-mp="2gz9y01py6bkg">My recommendation:</strong>{' '}
          Direction A (Almanac) or C (Blueprint) will move the furthest from
          “cheap” and feel the most editorial and ownable. Direction D is the
          safe upgrade if we want to keep the existing scroll choreography
          intact. Tell me which to pursue — or mix, e.g. Almanac framing with
          Gallery-level restraint — and I’ll build it directly into the section.
        </div>
      </div>
    </main>;
}